import { useEffect, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';

import { COMMUNICATION_PERMISSIONS } from '../api/communication';
import type { Me, MenuNode } from '../api/types';
import type { TranslationKey } from '../i18n';
import { brandingName } from '../api/branding';
import { useBranding } from '../config';
import { useSession } from '../session/useSession';
import { Bell } from './communication/Bell';
import { Avatar, IconButton, IconLink } from './controls';
import { Icon } from './Icon';
import { MenuTree } from './MenuTree';
import { TableExporter } from './tableExport';
import { TrailContext } from './trail';

/**
 * Le libellé de l'entrée de menu qui mène ici : l'entrée exacte, ou la plus
 * longue dont la route est un PRÉFIXE du chemin — la fiche
 * /administration/users/<id> prend le titre de « Comptes ».
 *
 * C'est lui qui titre l'onglet et la barre du haut, plutôt qu'un titre déclaré
 * dans le front : il appartient à l'établissement (D19) et arrive traduit.
 */
function labelFor(nodes: readonly MenuNode[], path: string): { label: string; route: string } | null {
    let best: { label: string; route: string; length: number } | null = null;

    const visit = (list: readonly MenuNode[]): void => {
        for (const node of list) {
            const route = node.route ?? null;

            if (route !== null && (path === route || path.startsWith(`${route}/`)) && route.length > (best?.length ?? -1)) {
                best = { label: node.label, route, length: route.length };
            }

            visit(node.children);
        }
    };

    visit(nodes);

    return best;
}

/**
 * Les écrans de configuration, et ceux qui ont leur propre navigation (D46) :
 * le menu principal s'y replie en arrivant.
 */
const COLLAPSING_ROUTES: readonly string[] = [
    '/referential/',
    '/assessment/regulation',
    '/assessment/documents/produce',
    '/assessment/documents/settings',
    '/documents/produce/single',
    '/documents/settings',
    '/documents/templates/',
    '/administration/roles',
    '/administration/identity-providers',
    '/super-administration/',
    '/finance/prices',
    '/finance/services',
    '/finance/discounts',
    '/finance/schedules',
    // D62, D63 : l'emploi du temps et la grille des absences ont besoin de toute la largeur.
    '/planning/',
    '/attendance/',
];

function collapsingRoute(pathname: string): boolean {
    return COLLAPSING_ROUTES.some((route) => pathname.startsWith(route));
}

/**
 * La coquille.
 *
 *   bureau (> 900 px)   menu latéral, OUVERT ou RÉDUIT au choix de l'utilisateur ;
 *   téléphone, tablette  menu en TIROIR, ouvert par le bouton ☰ et refermé à
 *                        chaque navigation, sur Échap ou en touchant le fond.
 *
 * Le basculement entre les deux est fait par la feuille de style, pas par du
 * code qui mesurerait l'écran : un seul arbre de composants, aucune différence
 * de comportement selon la largeur au moment du chargement.
 */
export function AppLayout({ me }: { readonly me: Me }): ReactNode {
    const { t, signOut, chooseLocale } = useSession();
    const { pathname } = useLocation();
    // Le menu est replié au chargement ; ce que l'utilisateur en fait ensuite
    // vaut pour sa navigation, jusqu'au prochain écran de configuration.
    const [collapsed, setCollapsed] = useState(true);
    const chosen = useRef(true);
    const [drawer, setDrawer] = useState(false);
    const narrow = useNarrow();
    // Sur téléphone et tablette, le menu est un tiroir : il s'ouvre toujours en
    // entier, libellés compris, quel que soit l'état réduit choisi au bureau.
    const folded = collapsed && !narrow;
    const { assets: brand, server } = useBranding();
    const institutionName = brandingName(server, me.locale.code) ?? me.institution.name;
    const section = labelFor(me.menu, pathname);
    const [trail, setTrail] = useState<string | null>(null);
    const title = trail ?? section?.label ?? null;

    useEffect(() => {
        document.title = title === null ? t('app.name') : `${title} · ${t('app.name')}`;
    }, [title, t]);

    // Le tiroir se referme dès qu'on a choisi où aller.
    useEffect(() => {
        setDrawer(false);
    }, [pathname]);

    // D46 : un écran de configuration replie le menu en arrivant ; ailleurs, le
    // menu garde l'état que l'utilisateur avait choisi.
    useEffect(() => {
        setCollapsed(collapsingRoute(pathname) ? true : chosen.current);
    }, [pathname]);

    useEffect(() => {
        if (!drawer) {
            return undefined;
        }

        const escape = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') {
                setDrawer(false);
            }
        };

        document.addEventListener('keydown', escape);

        return () => document.removeEventListener('keydown', escape);
    }, [drawer]);

    function toggleCollapse(): void {
        setCollapsed((value) => {
            chosen.current = !value;

            return !value;
        });
    }

    const logo = folded ? (brand.emblem ?? brand.logo) : brand.logo;

    // Réduit, le menu défile : une infobulle dessinée en CSS y serait coupée.
    // Elle est donc posée en position fixe, à côté de l'entrée survolée ou focalisée.
    const [tip, setTip] = useState<{ text: string; top: number; left: number; rtl: boolean } | null>(null);

    function showTip(target: EventTarget): void {
        if (!folded || !(target instanceof Element)) {
            return;
        }

        const element = target.closest<HTMLElement>('[data-tip]');
        const text = element?.dataset.tip;

        if (element === null || text === undefined || text === '') {
            setTip(null);

            return;
        }

        const box = element.getBoundingClientRect();
        const rtl = getComputedStyle(element).direction === 'rtl';
        setTip({ text, top: box.top + box.height / 2, left: rtl ? box.left - 10 : box.right + 10, rtl });
    }

    useEffect(() => {
        if (!folded) {
            setTip(null);
        }
    }, [folded]);

    return (
        <div className={`shell${folded ? ' shell--collapsed' : ''}${drawer ? ' shell--drawer' : ''}`}>
            <div className="shell__backdrop" aria-hidden="true" onClick={() => setDrawer(false)} />

            <aside
                className="side"
                id="side-menu"
                aria-label={t('menu.label')}
                onMouseOver={(event) => showTip(event.target)}
                onMouseLeave={() => setTip(null)}
                onFocus={(event) => showTip(event.target)}
                onBlur={() => setTip(null)}
                onScroll={() => setTip(null)}
            >
                <div className="side__head">
                    {/*
                        Le retour à l'accueil passe par le logo, et non par une
                        entrée de menu : une entrée appartiendrait à l'établissement
                        (D19), qui pourrait la masquer et priver ses utilisateurs du
                        seul chemin de retour.
                    */}
                    <Link className="brand" to="/" aria-label={`${institutionName} · ${t('app.home')}`}>
                        {logo === undefined ? <span className="brand__name">{folded ? me.institution.code : institutionName}</span> : <img className="brand__logo" src={logo} alt="" />}
                    </Link>

                    <button
                        className="side__toggle"
                        type="button"
                        aria-label={collapsed ? t('menu.expand') : t('menu.collapse')}
                        data-tip={collapsed ? t('menu.expand') : t('menu.collapse')}
                        aria-expanded={!collapsed}
                        aria-controls="side-menu"
                        onClick={toggleCollapse}
                    >
                        <Icon name={collapsed ? 'panelOpen' : 'panelClose'} size={18} />
                    </button>

                    <button className="side__close" type="button" aria-label={t('menu.close')} onClick={() => setDrawer(false)}>
                        <Icon name="close" size={20} />
                    </button>
                </div>

                <nav className="side__nav" aria-label={t('menu.label')}>
                    {me.menu.length === 0 ? <p className="menu__empty">{t('menu.empty')}</p> : <MenuTree nodes={me.menu} collapsed={folded} />}
                </nav>

                {/* Sur téléphone, les commandes de la barre du haut passent ici. */}
                <div className="side__foot">
                    <Link className="side__user" to="/account">
                        <Avatar name={me.displayName} accountId={me.id} version={me.avatarVersion} />
                        <span>{me.displayName}</span>
                    </Link>
                    <Link className="side__action" to="/account">
                        <Icon name="user" size={20} />
                        {t('profile.open')}
                    </Link>
                    <button className="side__action side__action--danger" type="button" onClick={() => void signOut()}>
                        <Icon name="logout" size={20} />
                        {t('session.signOut')}
                    </button>
                </div>
            </aside>

            {tip === null ? null : (
                <div className={`side-tip${tip.rtl ? ' side-tip--rtl' : ''}`} aria-hidden="true" style={{ top: tip.top, left: tip.left }}>
                    {tip.text}
                </div>
            )}

            <div className="shell__main">
                <header className="topbar">
                    <button className="icon-button topbar__burger" type="button" aria-label={t('menu.open')} aria-expanded={drawer} aria-controls="side-menu" onClick={() => setDrawer(true)}>
                        <Icon name="menu" size={22} />
                    </button>

                    {/*
                        Le fil d'Ariane : la rubrique du menu, puis ce que l'écran
                        affiche. Sur téléphone, il se réduit à « ← Rubrique ».
                    */}
                    {trail !== null && section !== null && section.route !== pathname ? (
                        <nav className="topbar__title crumbs" aria-label={t('menu.trail')}>
                            <Link className="crumbs__parent" to={section.route}>
                                <Icon name="arrowLeft" size={20} />
                                <span>{section.label}</span>
                            </Link>
                            <span className="crumbs__separator" aria-hidden="true">
                                <Icon name="chevronRight" size={16} />
                            </span>
                            <span className="crumbs__current" aria-current="page">
                                <bdi>{trail}</bdi>
                            </span>
                        </nav>
                    ) : (
                        <span className="topbar__title">{title ?? t('app.name')}</span>
                    )}

                    {/* D67 : la cloche, pour qui détient une permission de la communication. */}
                    {me.permissions.some((code) => COMMUNICATION_PERMISSIONS.includes(code)) ? <Bell /> : null}

                    {/*
                        Le sélecteur n'offre que ce que l'établissement a activé :
                        la liste vient de /api/me, elle n'est pas construite ici.
                    */}
                    {me.locale.available.length > 1 ? (
                        <label className="topbar__language" data-tip={t('language.label')}>
                            <Icon name="globe" size={18} />
                            <span className="visually-hidden">{t('language.label')}</span>
                            <select value={me.locale.code} onChange={(event) => void chooseLocale(event.target.value)}>
                                {me.locale.available.map((language) => (
                                    <option key={language.code} value={language.code}>
                                        {language.nativeName}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ) : null}

                    <span className="topbar__desktop">
                        <IconLink icon="lock" label={t('session.changePassword')} to="/account/password" />
                    </span>
                    <span className="topbar__desktop">
                        <IconButton icon="logout" label={t('session.signOut')} onClick={() => void signOut()} />
                    </span>

                    <Link className="topbar__user" to="/account" aria-label={`${t('profile.open')} · ${me.displayName}`}>
                        <Avatar name={me.displayName} accountId={me.id} version={me.avatarVersion} />
                        <span className="topbar__name">{me.displayName}</span>
                    </Link>
                </header>

                <main className="content">
                    <TrailContext value={setTrail}>
                        <Outlet />
                    </TrailContext>
                    <TableExporter />
                </main>
                <PortalTabs menu={me.menu} pathname={pathname} onMore={() => setDrawer(true)} />
            </div>
        </div>
    );
}

/** Les onglets du bas, au téléphone, pour qui a « Mon espace » (D73) ; « Plus » ouvre le menu. */
const PORTAL_TABS: readonly { readonly route: string; readonly icon: string; readonly label: TranslationKey }[] = [
    { route: '/me', icon: 'home', label: 'port.tabs.home' },
    { route: '/me/timetable', icon: 'calendar', label: 'port.tabs.timetable' },
    { route: '/me/marks', icon: 'book', label: 'port.tabs.marks' },
    { route: '/notifications', icon: 'bell', label: 'port.tabs.messages' },
];

/** Ceux de l'enseignant : accueil, agenda, appels, notes. */
const TEACHER_TABS: readonly { readonly route: string; readonly icon: string; readonly label: TranslationKey }[] = [
    { route: '/teach', icon: 'home', label: 'port.tabs.home' },
    { route: '/planning/mine', icon: 'calendar', label: 'port.tabs.timetable' },
    { route: '/teach/rolls', icon: 'checks', label: 'tch.tabs.rolls' },
    { route: '/assessment/mine', icon: 'book', label: 'port.tabs.marks' },
];

function PortalTabs({ menu, pathname, onMore }: { readonly menu: readonly MenuNode[]; readonly pathname: string; onMore(): void }): ReactNode {
    const { t } = useSession();
    const portal = menu.find((node) => node.children.some((child) => child.route === '/me' || child.route === '/teach'));

    if (portal === undefined) {
        return null;
    }

    const routes = new Set(portal.children.map((child) => child.route));
    const tabs = (routes.has('/teach') ? TEACHER_TABS : PORTAL_TABS).filter((tab) => routes.has(tab.route));

    return (
        <nav className="port-tabs" aria-label={portal.label}>
            {tabs.map((tab) => (
                <Link key={tab.route} className={`port-tabs__tab${pathname === tab.route ? ' port-tabs__tab--on' : ''}`} to={tab.route} aria-current={pathname === tab.route ? 'page' : undefined}>
                    <Icon name={tab.icon} size={20} />
                    <span>{t(tab.label)}</span>
                </Link>
            ))}
            <button className="port-tabs__tab" type="button" onClick={onMore}>
                <Icon name="list" size={20} />
                <span>{t('port.tabs.more')}</span>
            </button>
        </nav>
    );
}

const NARROW = '(max-width: 900px)';

/** Vrai quand l'écran est assez étroit pour que le menu devienne un tiroir (base.css). */
function useNarrow(): boolean {
    const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(NARROW).matches);

    useEffect(() => {
        const query = window.matchMedia(NARROW);
        const update = (): void => setNarrow(query.matches);
        update();
        query.addEventListener('change', update);

        return () => query.removeEventListener('change', update);
    }, []);

    return narrow;
}
